import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Toaster } from 'sonner';
import './App.css';
import Rules from './pages/Rules';
import Home from './pages/Home';
import Auth from './pages/Auth';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AppShell from './components/layout/AppShell';
import { SeasonProvider } from './contexts/SeasonContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { Dashboard } from './pages/Dashboard';
import { Reveal } from './pages/Reveal';
import HistoryMatrix from './pages/HistoryMatrix';
import Account from './pages/Account';
import AdminHome from './pages/admin/AdminHome';
import AdminUsers from './pages/admin/AdminUsers';
import AdminConfig from './pages/admin/AdminConfig';
import AdminBroadcast from './pages/admin/AdminBroadcast';
import { formatDelta } from './utils/formatDelta';
import { formatKickoff } from './utils/formatKickoff';
import { computeAttritionRows } from './utils/attrition';

const EntriesPage = React.lazy(() => import('./pages/EntriesPage').then(m => ({ default: m.EntriesPage })));

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

export { formatDelta, formatKickoff, computeAttritionRows, Reveal, Dashboard };

function ThemedToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} richColors position="top-center" />;
}

function App() {
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem('token') || ''; } catch { return ''; }
  });
  const [me, setMe] = useState(null);

  const logout = () => {
    setToken('');
    setMe(null);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || me) return;
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          setToken('');
          return;
        }
        const data = await res.json();
        const user = data?.user ?? (data?.id ? data : null);
        if (!cancelled && user) setMe(user);
      } catch {
        if (!cancelled) setToken('');
      }
    })();
    return () => { cancelled = true; };
  }, [token, me]);

  useEffect(() => {
    if (token) {
      window.localStorage.setItem('token', token);
    } else {
      window.localStorage.removeItem('token');
    }
  }, [token]);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <SeasonProvider>
          <AppShell me={me} onLogout={logout}>
            <ThemedToaster />
            <Routes>
            <Route path="/auth" element={<Auth setToken={setToken} setMe={setMe} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={token ? <Dashboard token={token} me={me} onLogout={logout} /> : <Home />} />
            <Route path="/home" element={token ? <Navigate to="/" replace /> : <Home />} />
            <Route path="/entries" element={
              <Suspense fallback={<div className="mx-auto max-w-7xl px-4 py-6 text-muted-foreground">Loading…</div>}>
                <EntriesPage token={token} me={me} />
              </Suspense>
            } />
            <Route path="/entries/:entryId" element={
              <Suspense fallback={<div className="mx-auto max-w-7xl px-4 py-6 text-muted-foreground">Loading…</div>}>
                <EntriesPage token={token} me={me} />
              </Suspense>
            } />
            <Route path="/grid" element={<HistoryMatrix me={me} />} />
            <Route path="/info" element={<Rules me={me} />} />
            <Route path="/account" element={<Account token={token} me={me} setMe={setMe} />} />
            <Route path="/admin" element={<AdminHome token={token} me={me} />} />
            <Route path="/admin/payments" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/users" element={<AdminUsers token={token} me={me} setMe={setMe} />} />
            <Route path="/admin/config" element={<AdminConfig token={token} me={me} />} />
            <Route path="/admin/broadcast" element={<AdminBroadcast token={token} me={me} />} />
            <Route path="*" element={
              <div className="mx-auto max-w-7xl px-4 py-12">
                <h2 className="text-xl font-semibold">Page not found</h2>
                <Link to="/" className="text-primary">Return home</Link>
              </div>
            } />
          </Routes>
        </AppShell>
        </SeasonProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
